import {
  isChoosableGiftConfig,
  isFixedGiftConfig,
  isGifteeBoxGiftConfig
} from '@console/common/projectDraft/giftConfig';
import type { GifteeBoxGiftConfig } from '@console/reducers/projectJson/giftConfigs';
import { createSelector } from 'reselect';
import type { Contents } from '../../../common/api/graphql/getContentsGql';
import type { AppState } from '../reducers';
import type {
  GiftConfig,
  GiftConfigs
} from '../reducers/projectJson/giftConfigs';
import type { ProjectDraft } from '../reducers/selectedProjectDraft';

const getProjectDrafts = (state: AppState) => state.projectDrafts;
const getContents = (state: AppState) => state.contents;

export interface ProjectDraftForDisplay extends ProjectDraft {
  contentImageUrls: ContentImageUrl[];
}

export type ContentImageUrl = string | null;

const getProjectDraftsForDisplay = createSelector(
  [getProjectDrafts, getContents],
  (
    projectDrafts: ProjectDraft[],
    contents: Contents
  ): ProjectDraftForDisplay[] =>
    projectDrafts.map(projectDraft => {
      const giftConfigsContentImageUrls = getGiftConfigsContentImageUrls(
        projectDraft.projectJson.giftConfigs,
        contents
      );
      return {
        ...projectDraft,
        contentImageUrls: giftConfigsContentImageUrls
      };
    })
);

const getGiftConfigsContentImageUrls = (
  giftConfigs: GiftConfigs,
  contents: Contents
) =>
  giftConfigs.flatMap(giftConfig => {
    if (isFixedGiftConfig(giftConfig) || isChoosableGiftConfig(giftConfig)) {
      return getGiftConfigContentImageUrls(giftConfig, contents);
    } else if (isGifteeBoxGiftConfig(giftConfig)) {
      return getGifteeBoxTemplateImageUrl(giftConfig);
    }
    return null;
  });

const getGiftConfigContentImageUrls = (
  giftConfig: GiftConfig,
  contents: Contents
) =>
  giftConfig.selectedContents.map(selectedContent => {
    const content = contents.find(
      content => content.urlCode === selectedContent
    );
    return content ? content.imageUrl : null;
  });

const getGifteeBoxTemplateImageUrl = (
  gifteeBoxGiftConfig: GifteeBoxGiftConfig
) => gifteeBoxGiftConfig.selectedGifteeBoxTemplate?.imageUrl || null;

export default getProjectDraftsForDisplay;
